/**
 * Main App Component
 * Handles routing and layout with auth guards
 */

import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/auth';
import { ThemeProvider, ToastProvider, ErrorBoundary } from './components/ui';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import AppointmentsPage from './pages/AppointmentsPage';
import CustomersPage from './pages/CustomersPage';
import CustomerProfile from './pages/CustomerProfile';
import InvoicesPage from './pages/InvoicesPage';
import InvoiceCreate from './pages/InvoiceCreate';
import SearchResultsPage from './pages/SearchResults';
import ExpensesPage from './pages/ExpensesPage';
import ExpenseReportPage from './pages/ExpenseReport';
import PaymentsPage from './pages/PaymentsPage';
import ReconciliationPage from './pages/ReconciliationPage';
import RecordingsPage from './pages/RecordingsPage';

// Layout
import DashboardLayout from './components/layout/DashboardLayout';
import OnboardingTour from './components/OnboardingTour';

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'ADMIN') {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Placeholder for future pages
function PlaceholderPage({ title }: { title: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <h2 className="text-2xl font-semibold mb-2">{title}</h2>
      <p className="text-muted-foreground">This page is under construction.</p>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <ToastProvider>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<Navigate to="/login" replace />} />
          <Route path="/reset-password" element={<Navigate to="/login" replace />} />

          {/* Protected Routes */}
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <DashboardLayout>
                  <OnboardingTour />
                  <ErrorBoundary>
                    <Routes>
                      <Route path="/" element={<Navigate to="/dashboard" replace />} />
                      <Route path="/dashboard" element={<DashboardPage />} />
                      <Route path="/appointments" element={<AppointmentsPage />} />
                      <Route path="/customers" element={<CustomersPage />} />
                      <Route path="/customers/:id" element={<CustomerProfile />} />
                      <Route path="/invoices" element={<InvoicesPage />} />
                      <Route path="/invoices/new" element={<InvoiceCreate />} />
                      <Route path="/invoices/:id/edit" element={<InvoiceCreate />} />
                      <Route path="/payments" element={<PaymentsPage />} />
                      <Route path="/reconciliation" element={<ReconciliationPage />} />
                      <Route path="/search" element={<SearchResultsPage />} />
                      <Route path="/expenses" element={<ExpensesPage />} />
                      <Route path="/expenses/report" element={<ExpenseReportPage />} />
                      <Route path="/recordings" element={<RecordingsPage />} />
                      <Route path="/settings" element={<PlaceholderPage title="Settings" />} />
                      <Route path="*" element={<Navigate to="/dashboard" replace />} />
                    </Routes>
                  </ErrorBoundary>
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;
